import { Component, inject, signal, computed, AfterViewInit, ViewChild, NgZone, input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { ButtonsModule } from '@progress/kendo-angular-buttons';

// Core components
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';

// Services
import { ReportJobService } from 'src/app/core/services/reporting/report-job.service';
import { ReportingService } from 'src/app/core/services/reporting/reporting.service';
import { statusOptions } from './sample-data/history-sample-data';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';

/**
 * History Component
 * Displays report generation history with filtering and download actions
 * Supports multi-customer report downloads via modal
 */
@Component({
  selector: 'app-reporting-history',
  standalone: true,
  imports: [CommonModule, FormsModule, DatatableComponent, DialogModule, ButtonsModule],
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
})
export class HistoryComponent extends BaseComponent implements AfterViewInit {
  @ViewChild('historyTable') historyTable?: DatatableComponent;

  private reportJobService = inject(ReportJobService);
  private reportingService = inject(ReportingService);
  private ngZone = inject(NgZone);
  private route = inject(ActivatedRoute);

  // Store current query params for in-app download URL construction
  private currentQueryParams: Record<string, string> = {};

  // Bound event handler reference for cleanup
  private boundHandleTableClick = (e: Event) => this.handleTableClick(e);

  // Datatable configuration
  public datatableUrl = `${environment.apiUrl}/v2/reporting/api/report-job/by-organization`;
  public datatableColumns: any[] = [];
  public initialFilters: any = {};

  // Filter state
  public statusFilter: string = 'all';
  public reportTypeFilter: string = 'all';
  public dateRangeFilter: number = 30; // Default to last 30 days

  // Modal state
  public selectedJobForDownload = signal<any>(null);
  public selectedJobForCustomers = signal<any>(null);

  // Computed for header stats
  public totalReportsGenerated = computed(() => {
    // This would come from API in production
    return 0; // Placeholder
  });

  // Filter options
  public statusOptions = statusOptions;
  public reportTypeOptions = signal<{ value: string; label: string }[]>([
    { value: 'all', label: 'All Report Types' },
  ]);
  public organizationId = input<number | null>(null);

  constructor() {
    super();
    this.initializeDatatableColumns();
    this.setInitialFilters();
    this.loadReportTypes();
    this.captureQueryParams();
  }

  /**
   * Capture current query params for in-app download URL construction
   */
  private captureQueryParams(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.currentQueryParams = { ...params };
    });
  }

  /**
   * Build download URL with organizationId and account_type for in-app navigation
   * Only appends params if they exist in the current URL (FE navigation)
   */
  private buildDownloadUrl(baseUrl: string): string {
    if (!baseUrl) {
      return baseUrl;
    }

    // Only append params if we have organizationId (in-app navigation)
    if (!this.currentQueryParams['organizationId']) {
      return baseUrl;
    }

    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('organizationId', this.currentQueryParams['organizationId']);
    if (this.currentQueryParams['account_type']) {
      url.searchParams.set('account_type', this.currentQueryParams['account_type']);
    }

    return url.toString();
  }

  /**
   * Load report types from API
   */
  private loadReportTypes(): void {
    this.reportingService.getReportTypes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (reportTypes) => {
        const options = [
          { value: 'all', label: 'All Report Types' },
          ...reportTypes.map((rt) => ({
            value: rt.reportTemplate.reportType,
            label: rt.name,
          })),
        ];
        this.reportTypeOptions.set(options);
      },
      error: (error) => {
        console.error('Error loading report types:', error);
      },
    });
  }

  ngAfterViewInit(): void {
    // Set up event delegation for action buttons
    // Use document level delegation since datatables renders rows dynamically
    setTimeout(() => {
      document.addEventListener('click', this.boundHandleTableClick);
    }, 500);

    // Clean up the event listener when component is destroyed
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('click', this.boundHandleTableClick);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organizationId'] && !changes['organizationId'].firstChange) {
      this.clearFilters();
      this.historyTable?.reload();
    }
  }

  /**
   * Initialize datatable columns configuration
   */
  private initializeDatatableColumns(): void {
    this.datatableColumns = [
      {
        data: 'report_template',
        name: 'report_template__name',
        title: 'Report Type',
        type: 'string',
        render: (data: any, _type: any, row: any) => {
          const reportName = data?.name || 'Unknown Report';
          const jobId = row.id || '—';
          return `
            <div>
              <div class="fw-semibold">${reportName}</div>
              <div class="text-muted small">Job ID: ${jobId}</div>
            </div>
          `;
        },
      },
      {
        data: 'job_results',
        name: 'job_results',
        title: 'Customers',
        type: 'string',
        orderable: false,
        render: (data: any[] | null, _type: any, row: any) => {
          const jobResults = data || [];

          if (jobResults.length === 0) {
            return '<span class="text-muted">—</span>';
          }

          const firstOrg = jobResults[0]?.organization?.name || '';
          const orgCount = this.getOrganizationCount(jobResults);
          const isAggregate = this.isAggregateResult(jobResults[0]);
          const label = orgCount === 1 ? 'customer' : 'customers';
          const aggregateTag = isAggregate
            ? ' <span class="badge bg-info text-white ms-1" style="font-size: 0.7em;">Aggregated</span>'
            : '';

          let customerCountHtml = `${orgCount} ${label}`;
          if (isAggregate) {
            const jobId = row.id || '';
            customerCountHtml = `<span class="action-view-customers" data-id="${jobId}" style="cursor: pointer; text-decoration: underline dotted;">${orgCount} ${label}</span>`;
          }

          return `
            <div>
              <div class="fw-semibold">${firstOrg}</div>
              <div class="text-muted small">${customerCountHtml}${aggregateTag}</div>
            </div>
          `;
        },
      },
      {
        data: 'job_results',
        name: 'status',
        title: 'Status',
        type: 'string',
        orderable: false,
        render: (data: any[] | null) => {
          const jobResults = data || [];
          if (jobResults.length === 0) {
            return '<span class="text-muted">—</span>';
          }

          // Derive overall status from job_results
          const statuses = jobResults.map((r: any) => r.status);
          let overallStatus = 'Queued';

          if (statuses.every((s: string) => s === 'Completed')) {
            overallStatus = 'Completed';
          } else if (statuses.some((s: string) => s === 'Failed')) {
            overallStatus = 'Failed';
          } else if (statuses.some((s: string) => s === 'Processing')) {
            overallStatus = 'Processing';
          }

          const statusConfig: { [key: string]: { class: string; icon: string } } = {
            Completed: { class: 'bg-success', icon: '✓' },
            Failed: { class: 'bg-danger', icon: '✗' },
            Processing: { class: 'bg-primary', icon: '⟳' },
            Queued: { class: 'bg-info', icon: '⏳' },
          };

          const config = statusConfig[overallStatus] || { class: 'bg-secondary', icon: '◯' };

          return `
            <span class="badge ${config.class}">
              ${config.icon} ${overallStatus}
            </span>
          `;
        },
      },
      {
        data: 'created_at',
        name: 'created_at',
        title: 'Created',
        type: 'date',
        render: (data: string | null) => {
          if (!data) {
            return '<span class="text-muted">—</span>';
          }
          const date = new Date(data);
          const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          return `
            <div>
              <div>${dateStr}</div>
              <div class="text-muted small">${timeStr}</div>
            </div>
          `;
        },
      },
      {
        data: 'job_results',
        name: 'completed_at',
        title: 'Completed',
        type: 'date',
        orderable: false,
        render: (data: any[] | null) => {
          const jobResults = data || [];
          // Find the latest completed_at from job_results
          const completedDates = jobResults
            .map((r: any) => r.completed_at)
            .filter((d: any) => d);

          if (completedDates.length === 0) {
            return '<span class="text-muted">—</span>';
          }

          const latestDate = new Date(Math.max(...completedDates.map((d: string) => new Date(d).getTime())));
          return latestDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        },
      },
      {
        data: 'job_results',
        name: 'results',
        title: 'Results',
        type: 'string',
        orderable: false,
        render: (data: any[] | null) => {
          const jobResults = data || [];
          if (jobResults.length === 0) {
            return '<span class="text-muted">—</span>';
          }

          const successful = jobResults.filter((r: any) => r.status === 'Completed').length;
          const failed = jobResults.filter((r: any) => r.status === 'Failed').length;

          const badges = [];
          if (successful > 0) {
            badges.push(`<span class="badge bg-success">${successful} successful</span>`);
          }
          if (failed > 0) {
            badges.push(`<span class="badge bg-danger">${failed} failed</span>`);
          }

          return badges.length > 0 ? badges.join(' ') : '<span class="text-muted">—</span>';
        },
      },
      {
        data: 'id',
        name: 'actions',
        title: 'Actions',
        type: 'actions',
        orderable: false,
        render: (data: string, _type: any, row: any) => {
          const buttons = [];
          const jobResults = row.job_results || [];

          // Check if downloads are available (zip file or any individual report)
          const hasDownloadAllUrl = !!row.download_all_url;
          const hasAnyDownloadableResult = jobResults.some((r: any) => r.download_url);
          const canDownload = hasDownloadAllUrl || hasAnyDownloadableResult;

          // Download button (when any downloads are available)
          if (canDownload) {
            const downloadLabel = jobResults.length > 1 ? 'Download Options' : 'Download';
            buttons.push(`
              <button class="btn btn-sm btn-outline-primary action-download" data-id="${data}" title="Download Report">
                📥 ${downloadLabel}
              </button>
            `);
          }

          return `<div class="btn-group btn-group-sm" role="group">${buttons.join('')}</div>`;
        },
      },
    ];
  }

  /**
   * Set initial filters
   */
  private setInitialFilters(): void {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - this.dateRangeFilter);

    const filters: any = {
      date_range_start: this.formatDateForApi(startDate),
      date_range_end: this.formatDateForApi(endDate),
    };

    // Build OData $filter string
    const odataFilters = this.buildODataFilter();
    if (odataFilters) {
      filters['$filter'] = odataFilters;
    }

    this.initialFilters = filters;
  }

  /**
   * Handle filter changes
   */
  public onFilterChange(): void {
    if (!this.historyTable) return;

    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - this.dateRangeFilter);

    const filters: any = {
      date_range_start: this.formatDateForApi(startDate),
      date_range_end: this.formatDateForApi(endDate),
    };

    // Build OData $filter string
    const odataFilters = this.buildODataFilter();
    if (odataFilters) {
      filters['$filter'] = odataFilters;
    }

    this.historyTable.setFilters(filters);
  }

  /**
   * Build OData $filter string from current filter selections
   */
  private buildODataFilter(): string {
    const conditions: string[] = [];

    if (this.statusFilter !== 'all') {
      conditions.push(`result_status eq '${this.statusFilter}'`);
    }

    if (this.reportTypeFilter !== 'all') {
      conditions.push(`report_type eq '${this.reportTypeFilter}'`);
    }

    return conditions.join(' and ');
  }

  /**
   * Clear all filters
   */
  public clearFilters(): void {
    this.statusFilter = 'all';
    this.reportTypeFilter = 'all';
    this.dateRangeFilter = 30;
    this.onFilterChange();
  }

  /**
   * Set quick date range filter
   */
  public setQuickDateRange(days: number): void {
    this.dateRangeFilter = days;
    this.onFilterChange();
  }

  /**
   * Handle table click events (action buttons)
   */
  private handleTableClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Download action
    if (target.classList.contains('action-download') || target.closest('.action-download')) {
      console.log('Download button clicked');
      event.preventDefault();
      event.stopPropagation();

      const button = target.classList.contains('action-download') ? target : target.closest('.action-download');
      const jobId = button?.getAttribute('data-id');
      console.log('Job ID from button:', jobId);

      if (jobId) {
        this.handleDownloadClick(jobId);
      }
      return;
    }

    // View aggregated customers action
    if (target.classList.contains('action-view-customers') || target.closest('.action-view-customers')) {
      const el = target.classList.contains('action-view-customers') ? target : target.closest('.action-view-customers');
      const jobId = el?.getAttribute('data-id');
      if (jobId) {
        this.openCustomersModal(jobId);
      }
      return;
    }

    // Retry action
    if (target.classList.contains('action-retry') || target.closest('.action-retry')) {
      const button = target.classList.contains('action-retry') ? target : target.closest('.action-retry');
      const jobId = button?.getAttribute('data-id');
      if (jobId) {
        this.retryJob(jobId);
      }
    }

    // Delete action
    if (target.classList.contains('action-delete') || target.closest('.action-delete')) {
      const button = target.classList.contains('action-delete') ? target : target.closest('.action-delete');
      const jobId = button?.getAttribute('data-id');
      if (jobId) {
        this.deleteJob(jobId);
      }
    }
  }

  /**
   * Handle download click - opens modal for multi-customer jobs
   */
  private handleDownloadClick(jobId: string): void {
    console.log('Download clicked for job:', jobId);

    // Run inside Angular zone to ensure change detection
    this.ngZone.run(() => {
      this.reportJobService.getReportJob(jobId).subscribe({
        next: (job: any) => {
          if (!job) {
            console.error('Job not found:', jobId);
            return;
          }

          console.log('Job fetched:', job);
          const jobResults = job.job_results || [];
          console.log('Customer count:', jobResults.length);

          // If single customer, download directly
          if (jobResults.length === 1 && jobResults[0].download_url) {
            console.log('Single customer - downloading directly');
            window.open(this.buildDownloadUrl(jobResults[0].download_url), '_blank');
          } else {
            // If multiple customers, show modal
            console.log('Multiple customers - showing modal');
            this.selectedJobForDownload.set(job);
            console.log('Modal signal set:', this.selectedJobForDownload());
          }
        },
        error: (error) => {
          console.error('Error fetching job:', error);
        },
      });
    });
  }

  /**
   * Close download modal
   */
  public closeDownloadModal(): void {
    this.selectedJobForDownload.set(null);
  }

  /**
   * Open modal listing all organizations included in an aggregated report
   */
  private openCustomersModal(jobId: string): void {
    this.ngZone.run(() => {
      this.reportJobService.getReportJob(jobId).subscribe({
        next: (job: any) => {
          if (!job) {
            console.error('Job not found:', jobId);
            return;
          }
          this.selectedJobForCustomers.set(job);
        },
        error: (error) => {
          console.error('Error fetching job:', error);
        },
      });
    });
  }

  /**
   * Close aggregated customers modal
   */
  public closeCustomersModal(): void {
    this.selectedJobForCustomers.set(null);
  }

  /**
   * Get id/name pairs for every organization included in an aggregated
   * report job, from the customers field returned by the backend.
   */
  public getAggregatedCustomers(job: any): { id: number; name: string }[] {
    return job?.job_results?.[0]?.customers || [];
  }

  /**
   * Download all reports as ZIP using the download_all_url
   */
  public downloadAllReports(job: any): void {
    if (job.download_all_url) {
      window.open(this.buildDownloadUrl(job.download_all_url), '_blank');
    }
  }

  /**
   * Download single customer report using the download_url from job_results
   */
  public downloadSingleReport(downloadUrl: string): void {
    if (downloadUrl) {
      window.open(this.buildDownloadUrl(downloadUrl), '_blank');
    }
  }

  /**
   * Get customer details for modal display from job_results
   */
  public getCustomerDetails(job: any): { id: number; name: string; downloadUrl: string | null }[] {
    const jobResults = job.job_results || [];
    return jobResults.map((result: any) => ({
      id: result.organization?.id || 0,
      name: result.organization?.name || 'Unknown',
      downloadUrl: result.download_url,
    }));
  }

  /**
   * Get the real organization count from job results.
   * For aggregate reports, a single result contains multiple organizations
   * in input_data.params.organizations; fall back to result count otherwise.
   */
  public getOrganizationCount(jobResults: any[]): number {
    if (jobResults.length === 1 && this.isAggregateResult(jobResults[0])) {
      const orgs = jobResults[0]?.input_data?.params?.organizations;
      if (Array.isArray(orgs)) {
        return orgs.length;
      }
    }
    return jobResults.length;
  }

  /**
   * Whether a job is an aggregate report, determined by the aggregate_report
   * flag in the job result's input_data.params.
   */
  public isAggregateJob(job: any): boolean {
    const jobResults = job?.job_results || [];
    return jobResults.length === 1 && this.isAggregateResult(jobResults[0]);
  }

  private isAggregateResult(result: any): boolean {
    return result?.input_data?.params?.aggregate_report === true;
  }

  /**
   * Get organization count for the currently selected download modal job.
   */
  public getModalOrganizationCount(): number {
    const job = this.selectedJobForDownload();
    if (!job) return 0;
    return this.getOrganizationCount(job.job_results || []);
  }

  /**
   * Retry failed job
   */
  private retryJob(jobId: string): void {
    this.reportJobService.retryReportJob(jobId).subscribe({
      next: (newJob) => {
        console.log('Job retried:', newJob);
        // Refresh datatable by reapplying filters (triggers reload)
        this.onFilterChange();
      },
      error: (error) => {
        console.error('Error retrying job:', error);
      },
    });
  }

  /**
   * Delete job
   */
  private deleteJob(jobId: string): void {
    if (!confirm('Are you sure you want to delete this report job? This cannot be undone.')) {
      return;
    }

    this.reportJobService.deleteReportJob(jobId).subscribe({
      next: () => {
        console.log('Job deleted:', jobId);
        // Refresh datatable by reapplying filters (triggers reload)
        this.onFilterChange();
      },
      error: (error) => {
        console.error('Error deleting job:', error);
      },
    });
  }

  /**
   * Format date for API
   */
  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
